import * as Sentry from "@sentry/react";

import { GENERIC_ERROR } from "constants/errors";
import { ValidationError } from "utils/yup";

const ARRAY_INDEX_PATTERN = /\[\d+\]/g;
const ARRAY_INDEX_PLACEHOLDER = "[]";
const ERROR_NAME = "ResponseValidationError";
const ID_PLACEHOLDER = "/:id";
const ID_SEGMENT_PATTERN =
  /\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32}|\d+)(?=\/|$)/gi;
const MAX_ISSUES_IN_MESSAGE = 5;
const MAX_REPORTED_ISSUES = 20;
const MESSAGE_PREFIX = "Response validation failed";
const ORIGIN_PATTERN = /^[a-z]+:\/\/[^/]+/i;
const ROOT_PATH = "<root>";
const UNKNOWN_ENDPOINT = "<unknown endpoint>";

interface ValidationIssue {
  message: string;
  path: string;
  type: string | undefined;
  valueType: string;
}

// Structurally compatible with both `RTKMeta` and `FetchBaseQueryMeta`
export interface ValidationMeta {
  request?: {
    method?: string;
    url?: string;
  };
}

/**
 * Yup messages are translation keys (ie. `FIELD_REQUIRED`), so a raw
 * `ValidationError` tells neither which field failed nor how many did.
 * All of them end up grouped as a single Sentry issue.
 *
 * This reports an error which carries the failing endpoint and paths in its
 * message and fingerprint, so every broken field gets its own Sentry issue.
 * A root level failure (ie. an empty response body) has no path at all, so the
 * endpoint is the only thing telling such reports apart.
 *
 * Values are never sent, only their types, as responses may contain personal
 * data. The same goes for the endpoint - query string is dropped and id
 * segments are replaced with `:id`.
 */
export function reportValidationError(error: unknown, meta?: ValidationMeta) {
  if (!(error instanceof ValidationError)) {
    Sentry.captureException(error);

    return;
  }

  const issues = getUniqueIssues(collectIssues(error));
  const paths = [...new Set(issues.map(({ path }) => path))];
  const endpoint = getEndpoint(meta);
  const reportedError = new Error(buildMessage(endpoint, issues), {
    cause: error,
  });

  reportedError.name = ERROR_NAME;

  Sentry.captureException(reportedError, {
    fingerprint: [ERROR_NAME, endpoint, ...paths],
    extra: {
      endpoint,
      issues: issues.slice(0, MAX_REPORTED_ISSUES),
      issuesCount: issues.length,
    },
  });
}

function getEndpoint(meta?: ValidationMeta) {
  const { method, url } = meta?.request ?? {};

  if (!url) {
    return UNKNOWN_ENDPOINT;
  }

  const [path] = url.replace(ORIGIN_PATTERN, "").split("?");

  return `${method ?? ""} ${path.replace(ID_SEGMENT_PATTERN, ID_PLACEHOLDER)}`.trim();
}

function collectIssues(error: ValidationError): ValidationIssue[] {
  if (error.inner.length) {
    return error.inner.flatMap(collectIssues);
  }

  return [
    {
      message: getMessage(error.message),
      path: normalizePath(error.path),
      type: error.type,
      valueType: getValueType(error.value),
    },
  ];
}

// Some locale entries return `{ key, values }` instead of a plain string
function getMessage(message: ValidationError["message"]) {
  if (typeof message === "string") {
    return message;
  }

  const { key } = message as unknown as { key?: string };

  return key ?? GENERIC_ERROR;
}

function getUniqueIssues(issues: ValidationIssue[]) {
  const uniqueIssues = new Map(
    issues.map((issue) => [`${issue.path}|${issue.message}`, issue])
  );

  return [...uniqueIssues.values()];
}

function normalizePath(path: string | undefined) {
  if (!path) {
    return ROOT_PATH;
  }

  return path.replace(ARRAY_INDEX_PATTERN, ARRAY_INDEX_PLACEHOLDER);
}

function getValueType(value: unknown) {
  if (value === null) {
    return "null";
  }

  if (Array.isArray(value)) {
    return "array";
  }

  return typeof value;
}

function buildMessage(endpoint: string, issues: ValidationIssue[]) {
  const listedIssues = issues
    .slice(0, MAX_ISSUES_IN_MESSAGE)
    .map(({ message, path }) => `${path} (${message})`)
    .join(", ");
  const notListedIssuesCount = issues.length - MAX_ISSUES_IN_MESSAGE;
  const prefix = `${MESSAGE_PREFIX} for ${endpoint}:`;

  if (notListedIssuesCount > 0) {
    return `${prefix} ${listedIssues} and ${notListedIssuesCount} more`;
  }

  return `${prefix} ${listedIssues}`;
}
